import { ObjectType } from 'interfaces';
import { action, observable } from 'mobx';

export type PartsDataViewScope =
  | ObjectType.OC
  | ObjectType.PO
  | ObjectType.OI
  | ObjectType.Payment
  | ObjectType.CL
  | ObjectType.QC
  | ObjectType.SH
  | ObjectType.SU
  | ObjectType.J
  | ObjectType.II;

const STORAGE_PREFIX = 'partsView.showAllData';

const encodeScope = (scope: PartsDataViewScope) => encodeURIComponent(scope);
const getStorageKey = (scope: PartsDataViewScope) => `${STORAGE_PREFIX}.${encodeScope(scope)}`;

const readShowAllFromStorage = (scope: PartsDataViewScope): boolean | null => {
  try {
    const raw = localStorage.getItem(getStorageKey(scope));

    if (raw === null) return null;

    return raw === 'true';
  } catch {
    return null;
  }
};

const writeShowAllToStorage = (scope: PartsDataViewScope, value: boolean) => {
  try {
    localStorage.setItem(getStorageKey(scope), String(value));
  } catch {
    return;
  }
};

class PartsDataViewStore {
  @observable showAllData: Record<PartsDataViewScope, boolean> = {
    [ObjectType.OC]: false,
    [ObjectType.PO]: false,
    [ObjectType.II]: false,
    [ObjectType.CL]: false,
    [ObjectType.SU]: false,
    [ObjectType.SH]: false,
    [ObjectType.OI]: false,
    [ObjectType.J]: false,
    [ObjectType.Payment]: false,
    [ObjectType.QC]: true,
  };

  constructor() {
    this.hydrateFromStorage();
  }

  @action
  private hydrateFromStorage() {
    (Object.keys(this.showAllData) as PartsDataViewScope[]).forEach((scope) => {
      const stored = readShowAllFromStorage(scope);

      if (stored !== null) {
        this.showAllData[scope] = stored;
      }
    });
  }

  isHidden = (scope: PartsDataViewScope) => !this.showAllData[scope];

  @action
  toggleView = (scope: PartsDataViewScope) => {
    const next = !this.showAllData[scope];
    this.showAllData[scope] = next;
    writeShowAllToStorage(scope, next);
  };
}

export default PartsDataViewStore;
